'use client';

import { useEffect, useState } from 'react';

type SubmitState =
  | { type: 'idle' }
  | { type: 'submitting' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string };

export default function ShopifyCourseWaitlistForm() {
  const [state, setState] = useState<SubmitState>({ type: 'idle' });
  const [startedAt, setStartedAt] = useState('');
  useEffect(() => {
    setStartedAt(String(Date.now()));
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ type: 'submitting' });

    const form = event.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name')?.toString().trim() || '';
    const email = formData.get('email')?.toString().trim() || '';
    const goals = formData.get('goals')?.toString().trim() || '';

    if (!name || !email || !goals) {
      setState({
        type: 'error',
        message: 'Add your name, email, and goals before joining the waitlist.',
      });
      return;
    }

    try {
      const response = await fetch('/api/shopify-course-waitlist', {
        method: 'POST',
        body: formData,
      });

      const payload = (await response.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
      } | null;

      if (!response.ok || !payload?.ok) {
        setState({
          type: 'error',
          message: payload?.message || 'The waitlist request could not be sent right now.',
        });
        return;
      }

      form.reset();
      setState({
        type: 'success',
        message:
          payload.message ||
          'Joined the waitlist. Cleanor Labs will follow up from hello@cleanor.app.',
      });
    } catch {
      setState({
        type: 'error',
        message: 'The request failed. Please try again in a moment.',
      });
    }
  }

  return (
    <form className="support-form shopify-waitlist-form" onSubmit={handleSubmit}>
      <input type="hidden" name="startedAt" value={startedAt} />
      <div className="support-form-honeypot" aria-hidden="true">
        <label htmlFor="waitlist-company">Company</label>
        <input autoComplete="off" id="waitlist-company" name="company" tabIndex={-1} type="text" />
      </div>
      <div className="support-form-grid">
        <label className="field">
          <span>Name</span>
          <input
            autoComplete="name"
            maxLength={80}
            minLength={2}
            name="name"
            placeholder="Your name"
            required
            type="text"
          />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            autoComplete="email"
            maxLength={120}
            name="email"
            placeholder="you@example.com"
            required
            type="email"
          />
        </label>
      </div>

      <div className="support-form-grid">
        <label className="field">
          <span>Store URL</span>
          <input
            inputMode="url"
            maxLength={160}
            name="storeUrl"
            placeholder="yourstore.com"
            type="text"
          />
        </label>

        <label className="field">
          <span>Role</span>
          <select defaultValue="merchant" name="role">
            <option value="merchant">Merchant</option>
            <option value="catalog-manager">Catalog manager</option>
            <option value="agency">Agency</option>
            <option value="freelancer">Freelancer</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>

      <label className="field field-textarea">
        <span>What do you want from the course?</span>
        <textarea
          maxLength={1000}
          minLength={12}
          name="goals"
          placeholder="Product image standards, filename cleanup, batch exports, PDP visuals, or anything else you want covered."
          required
          rows={6}
        />
      </label>

      <div className="support-form-footer">
        <p className="support-form-note">
          This form sends your course request directly to hello@cleanor.app so the team can follow
          up with the right merchants first.
        </p>
        <button className="button" disabled={state.type === 'submitting'} type="submit">
          {state.type === 'submitting' ? 'Joining...' : 'Join waitlist'}
        </button>
      </div>

      {state.type === 'success' ? (
        <p className="support-feedback support-feedback-success">{state.message}</p>
      ) : null}
      {state.type === 'error' ? (
        <p className="support-feedback support-feedback-error">{state.message}</p>
      ) : null}
    </form>
  );
}
